import { useTranslation } from 'react-i18next';

import { ResultSection } from './ResultRow';
import { scoredMetrics } from '../../metricGrades';
import { overallScore } from '../../record';
import { resultPoints } from '../../resultCopy';
import { MetricCard } from '../MetricCard';
import { VocalTypeLine } from './VocalTypeLine';

import type { DiagnosisRecord } from '../../record';
import type { ResultPointId } from '../../resultCopy';

/**
 * 종합(덱 p10): 총점 · 보컬 타입 · 강점/개선점.
 *
 * 총점은 **홈과 같은 함수**(`overallScore`)에서 나온다 — 여기서 다시 평균을 내면 같은
 * 회차가 홈에서 64, 결과에서 65가 된다. 축 카드는 `scoredMetrics`가 정본이고 **회차와 무관하게
 * 같다**: 노래를 부른 회차라고 음정 정확도 축이 승격되지 않는다(그 축의 값은 언제나 지속음
 * 프록시다 — metricGrades 모듈 주석). 레퍼런스 대비 채점은 진단 3의 별개 항목이다.
 */

const PointList = ({
  title,
  points,
  tone,
}: {
  title: string;
  points: ResultPointId[];
  tone: 'strong' | 'weak';
}) => {
  const { t } = useTranslation();
  if (points.length === 0) {
    return null;
  }
  return (
    <div className="mt-4">
      <h4 className="text-xs font-semibold text-muted-foreground">{title}</h4>
      <ul className="mt-2 flex flex-col gap-1.5">
        {points.map((id) => (
          <li key={id} className="flex gap-2 text-sm">
            <span
              aria-hidden="true"
              className={
                tone === 'strong' ? 'text-primary' : 'text-muted-foreground'
              }
            >
              •
            </span>
            {t(`web.result.summary.points.${id}.${tone}`)}
          </li>
        ))}
      </ul>
    </div>
  );
};

export const SummarySection = ({ record }: { record: DiagnosisRecord }) => {
  const { t } = useTranslation();
  const overall = overallScore(record);
  const points = resultPoints(record);
  const cards = scoredMetrics();

  return (
    <ResultSection title={t('web.result.summary.section')}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-sm font-semibold">
          {t('web.result.summary.total')}
        </span>
        <span className="text-3xl font-bold tabular-nums">
          {overall === null
            ? t('web.result.pending')
            : t('web.result.score', { value: overall })}
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {t('web.result.summary.uncalibrated')}
      </p>

      <VocalTypeLine record={record} />

      <PointList
        title={t('web.result.summary.strengths')}
        points={points.strengths}
        tone="strong"
      />
      <PointList
        title={t('web.result.summary.improvements')}
        points={points.improvements}
        tone="weak"
      />

      <h4 className="mt-5 text-xs font-semibold text-muted-foreground">
        {t('web.result.summary.axes')}
      </h4>
      <div className="mt-2 flex flex-col gap-3">
        {cards.map(({ key, card }) => {
          const val = record.scores.find((s) => s.key === key)?.val;
          return val == null ? null : (
            <MetricCard key={key} card={card} val={val} />
          );
        })}
      </div>
    </ResultSection>
  );
};
